import Debug from 'debug';
import { useFormikContext } from 'formik';
import * as React from 'react';
import { Col as BCol, Row as BRow, FormGroup, FormLabel } from 'react-bootstrap';
import { Entity, Field, getFieldArray, getFieldColumnWidths, isFieldEditable, Row } from 'gossamer-unified';
import { ErrorBoundary, Section } from '../index';
import { FormField } from './FormField';
import styles from '../sections/FieldBlock.module.css';

const debug = Debug('gfe/se/FormBlock');

interface FormBlockProps<T extends Row> {
  alias?: string;
  entity: Entity<T>;
  fieldGroup?: string;
  isCreate: boolean;
  layout?: 'horizontal' | 'vertical';
}

export const FormBlock = <T extends {}>(props: FormBlockProps<T>): React.JSX.Element => {
  const identifier = props.alias || props.entity.id;
  const { values } = useFormikContext<Record<string, unknown>>();
  const recordValues = values as Record<string, unknown>;
  const sectionValues = recordValues[identifier] as Record<string, unknown> | undefined;
  if (!sectionValues || typeof sectionValues !== 'object' || Array.isArray(sectionValues)) {
    throw new Error(`block incorrectly initialized: values[${identifier}] does not exist`);
  }
  const children =
    getFieldArray(props.entity)
      .map(
        ([field, fieldId]: [Field, string]) =>
          [field, fieldId, isFieldEditable(field, props.isCreate ? 'C' : 'U')] as [Field, string, boolean],
      )
      .filter(([field, fieldId, editable]) => {
        const fieldGroupCheck = !props.fieldGroup || props.fieldGroup === field.fieldGroup;
        const visibleAttrCheck =
          typeof field.visible === 'boolean' ? field.visible : editable || !!sectionValues[fieldId];
        return fieldGroupCheck && visibleAttrCheck;
      })
      .map(([field, fieldId, editable]) => {
        const fullId = [identifier, fieldId].join('.');
        return props.layout === 'vertical' ? (
          <div key={fieldId} className="mb20px">
            <FormBlockField editable={editable} field={field} fullId={fullId} />
          </div>
        ) : (
          <BCol key={fieldId} {...getFieldColumnWidths(field)}>
            <FormBlockField editable={editable} field={field} fullId={fullId} />
          </BCol>
        );
      }) || [];
  return props.layout === 'vertical' ? (
    <ErrorBoundary>{children}</ErrorBoundary>
  ) : (
    <ErrorBoundary>
      <BRow className="mb20px">{children}</BRow>
    </ErrorBoundary>
  );
};

interface FormBlockFieldProps {
  editable: boolean;
  field: Field;
  fullId: string;
}

export const FormBlockField = (props: FormBlockFieldProps) => {
  return (
    <FormGroup
      id={props.fullId.replace(/\./g, '-')}
      controlId={props.fullId}
      className={`${styles.FieldBlock} fieldBlock`}
    >
      <FormLabel>
        {props.field.label}
        {props.field.mandatory && props.editable ? ' *' : ''}
        {props.field.description && (
          <>
            &nbsp;&nbsp;<a title={props.field.description}>ⓘ</a>
          </>
        )}
      </FormLabel>
      <FormField editable={props.editable} field={props.field} fullId={props.fullId} />
    </FormGroup>
  );
};

interface CreateProps<T extends Row> {
  entity: Entity<T>;
  fieldGroup?: string;
  title?: string;
  subtitle?: string;
  valuesKey: string;
}

export const Create = <T extends Row>(props: CreateProps<T>): React.JSX.Element => (
  <Section title={props.title} subtitle={props.subtitle}>
    <FormBlock alias={props.valuesKey} entity={props.entity} fieldGroup={props.fieldGroup} isCreate={true} />
  </Section>
);

interface UpdateProps<T extends Row> {
  createOnNotFound?: boolean;
  entity: Entity<T>;
  fieldGroup?: string;
  title?: string;
  subtitle?: string;
  valuesKey: string;
}

export const Update = <T extends Row>(props: UpdateProps<T>): React.JSX.Element => (
  <Section title={props.title} subtitle={props.subtitle}>
    <FormBlock alias={props.valuesKey} entity={props.entity} fieldGroup={props.fieldGroup} isCreate={false} />
  </Section>
);
